import { ApolloLink, Observable } from '@apollo/client';
import * as opentelemetryFe from 'opentelemetry-fe';

const { context, trace, SpanStatusCode } = opentelemetryFe;

function getOperationType(
  operation: Parameters<
    NonNullable<ConstructorParameters<typeof ApolloLink>[0]>
  >[0]
): string {
  const def = operation.query.definitions.find(
    (d) => d.kind === 'OperationDefinition'
  );
  return def && 'operation' in def ? def.operation : 'unknown';
}

export default new ApolloLink((operation, forward) => {
  const tracer = opentelemetryFe.getSafeTracer('apollo-stack-hubspot');

  const operationType = getOperationType(operation);
  const spanName = operation.operationName
    ? `${operationType} ${operation.operationName}`
    : operationType;

  const attributes: Record<string, string | boolean> = {
    'graphql.operation.type': operationType,
    'db.system': 'graphql',
    'graphql.hub.cache_hit': false,
  };
  if (operation.operationName) {
    attributes['graphql.operation.name'] = operation.operationName;
    attributes['db.operation.name'] = operation.operationName;
  }

  const span = tracer.startSpan(spanName, { attributes });

  return new Observable((observer) => {
    let sub: { unsubscribe: () => void } | undefined;
    let spanEnded = false;
    const endSpan = () => {
      if (!spanEnded) {
        spanEnded = true;
        span.end();
      }
    };

    context.with(trace.setSpan(context.active(), span), () => {
      sub = forward(operation).subscribe({
        next(result) {
          const ctx = operation.getContext();
          if (ctx.correlationId) {
            span.setAttribute('graphql.hub.correlation_id', ctx.correlationId);
          }

          if (result.errors?.length) {
            for (const err of result.errors) {
              span.addEvent('graphql.error', {
                'error.message': err.message,
                ...(err.extensions?.errorType
                  ? { 'error.type': String(err.extensions.errorType) }
                  : {}),
              });
            }
          }

          span.setStatus({ code: SpanStatusCode.OK });
          endSpan();
          observer.next(result);
        },
        error(err) {
          const { correlationId } = operation.getContext();
          if (correlationId) {
            span.setAttribute('graphql.hub.correlation_id', correlationId);
          }
          span.recordException(err);
          span.setStatus({ code: SpanStatusCode.ERROR });
          endSpan();
          observer.error(err);
        },
        complete() {
          endSpan();
          observer.complete();
        },
      });
    });
    return () => {
      sub?.unsubscribe();
      endSpan();
    };
  });
});
